import * as React from 'react';
import styled from 'styled-components';
import { Caption, Body1 } from '@estee/elc-typography';
import { classes } from '../../utils/Classes';
import { VTO_SHADE_SELECT, VTO_SHADE_SWATCH } from '../../constants/DataTestIdAttributes';
import { IShade } from '../../interfaces/IVto';
import { observer } from 'mobx-react';
import { IVtoTranslations } from '../../../exported/vto/Vto';
import { translate } from '@estee/elc-service';

interface IVtoShadeItem {
    shade: IShade;
    selectedShade?: IShade;
    isPerfectShadeEnabled?: boolean;
    onClick(event: React.MouseEvent<HTMLDivElement>): void;
    showAllShadesVariant?: boolean;
    showPerfectShadesLabel?: boolean;
    translations?: IVtoTranslations;
    isMatchSaved?: boolean;
    savedMatch?: string;
}

const Wrapper = styled.div`
    text-align: center;
    &:focus {
        outline: none;
    }
`;

const ShadeSwatch = styled.div`
    height: 56px;
    width: 56px;
    margin: 0 auto;
`;

const perfectShadeLabelClassNames = classes('vto-perfect-shade-label');
const shadeSwatchClassNames = classes('vto-custom-shade-picker-swatch');
const shadeItemNameClassNames = classes('vto-custom-shade-picker-item-name');

export const VtoShadeItem = translate([
    'bestShadeMessage',
    'bestLabel',
    'lighterLabel',
    'darkerLabel',
    'coolerLabel',
    'warmerLabel'
])(
    observer(
        ({
            shade,
            selectedShade,
            isPerfectShadeEnabled,
            onClick,
            showAllShadesVariant,
            translations,
            isMatchSaved,
            showPerfectShadesLabel,
            savedMatch
        }: IVtoShadeItem) => {
            const {
                bestLabel,
                lighterLabel,
                darkerLabel,
                coolerLabel,
                warmerLabel
            } = translations as IVtoTranslations;
            const shadeItemWrapperClassNames = classes('vto-custom-shade', {
                'elc-vto-custom-shade-selected': selectedShade && selectedShade.name === shade.name,
                'elc-vto-all-shades-lighter-shade':
                    showAllShadesVariant && shade.perfectShadeLabel === lighterLabel,
                'elc-vto-all-shades-match-shade':
                    showAllShadesVariant && shade.perfectShadeLabel === bestLabel,
                'elc-vto-all-shades-darker-shade':
                    showAllShadesVariant && shade.perfectShadeLabel === darkerLabel,
                'elc-vto-all-shades-cooler-shade':
                    showAllShadesVariant && shade.perfectShadeLabel === coolerLabel,
                'elc-vto-all-shades-warmer-shade':
                    showAllShadesVariant && shade.perfectShadeLabel === warmerLabel,
                'elc-vto-match-shade-selected': isMatchSaved && savedMatch === shade.name
            });
            const showPerfectShadeLabel =
                isPerfectShadeEnabled || showAllShadesVariant || showPerfectShadesLabel;

            return (
                <Wrapper
                    onClick={onClick}
                    className={shadeItemWrapperClassNames}
                    data-test-id={VTO_SHADE_SELECT}
                >
                    {showPerfectShadeLabel && (
                        <Body1 className={perfectShadeLabelClassNames}>
                            {shade.perfectShadeLabel}
                        </Body1>
                    )}
                    <ShadeSwatch
                        data-test-id={VTO_SHADE_SWATCH}
                        className={shadeSwatchClassNames}
                        style={{ backgroundColor: shade.hexVal }}
                    />
                    <Caption className={shadeItemNameClassNames}>{shade.name}</Caption>
                </Wrapper>
            );
        }
    )
);
